import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft,
  Sparkles,
  FileText,
  Banknote,
  ShieldCheck,
  CheckCircle2,
  Loader2,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import { api } from "../lib/api";

const PROMISES = [
  {
    icon: Banknote,
    title: "Upload your bank statements",
    body: "Drop 3–12 months of bank statement PDFs. We support major US, UK, and Canadian banks.",
  },
  {
    icon: Sparkles,
    title: "AI builds your books",
    body: "We categorize every transaction — revenue, COGS, payroll, rent, utilities — and reconstruct your income statement and cash flow.",
  },
  {
    icon: FileText,
    title: "Get your CFO Grade — automatically",
    body: "No typing, no CSV exports, no bookkeeper. Just upload, review the AI's work, and get the same 24+ ratio grade you know.",
  },
  {
    icon: ShieldCheck,
    title: "Bank-grade privacy",
    body: "Encrypted in transit and at rest. Auto-deleted after 90 days unless you save. No data sharing, ever.",
  },
];

const TIERS = [
  {
    name: "Starter",
    price: "$99",
    sub: "/ month",
    blurb: "Solo operators and side-businesses keeping it simple.",
    features: [
      "Up to 100 transactions / month",
      "1 bank account",
      "Monthly CFO Grade refresh",
      "Categorized P&L + cash flow",
    ],
    accent: false,
  },
  {
    name: "Growth",
    price: "$199",
    sub: "/ month",
    blurb: "The sweet spot for most growing SMBs.",
    features: [
      "Up to 500 transactions / month",
      "Up to 3 bank accounts",
      "Weekly CFO Grade refresh",
      "Peer benchmark deep-dive",
      "Everything in Starter",
    ],
    accent: true,
  },
  {
    name: "Pro",
    price: "$399",
    sub: "/ month",
    blurb: "Multi-entity operators and serious finance teams.",
    features: [
      "Unlimited transactions",
      "Unlimited bank accounts",
      "Daily CFO Grade refresh",
      "Custom KPIs",
      "Everything in Growth",
    ],
    accent: false,
  },
];

export default function CFOBooksWaitlistPage() {
  const [email, setEmail] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [preorderStatus, setPreorderStatus] = useState(null);

  useEffect(() => {
    document.title = "CFO Books — Upload your bank statements, get instant financials";
    const desc =
      "Upload your bank statements and CFO Grade builds your income statement, cash flow, and letter grade automatically. Reserve your founding-member spot — $79/mo for 12 months, locked in.";
    upsertMeta("name", "description", desc);
    upsertMeta("property", "og:title", "CFO Books — Auto financials from your bank statements");
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", `${window.location.origin}/cfo-books`);
    upsertMeta("name", "twitter:title", "CFO Books — Auto financials from your bank statements");
    upsertMeta("name", "twitter:description", desc);
    upsertCanonical(`${window.location.origin}/cfo-books`);
  }, []);

  // Poll preorder status when returning from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("preorder_session_id");
    const cancelled = params.get("preorder_cancelled");
    if (cancelled) {
      toast.error("Reservation cancelled. Your spot wasn't held — try again.");
      window.history.replaceState({}, "", "/cfo-books");
      return;
    }
    if (!sid) return;
    let attempts = 0;
    let timer = null;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/cfo-books/preorder/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setPreorderStatus(r.data);
          setEmail(r.data.email || "");
          toast.success("Your founding-member spot is locked in.");
          window.history.replaceState({}, "", "/cfo-books");
          return;
        }
        if (attempts < 8) {
          timer = setTimeout(poll, 1500);
        } else {
          toast.error("We couldn't confirm your deposit. Refresh the page in a moment.");
        }
      } catch {
        if (attempts < 5) timer = setTimeout(poll, 1500);
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      // Reservation always goes through Stripe Checkout ($1 refundable deposit).
      // This is the ONLY path — no free email-only signups for new reservers.
      const res = await api.post("/cfo-books/preorder", {
        email: email.trim().toLowerCase(),
        origin_url: window.location.origin,
        business_type: businessType.trim(),
        notes: notes.trim(),
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not reserve your spot. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Couldn't process — please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain" data-testid="cfo-books-waitlist-page">
      <header className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to home
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 pt-6 pb-20 grid lg:grid-cols-12 gap-12 items-start">
        {/* LEFT — Pitch */}
        <div className="lg:col-span-7">
          <div
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C26D5C] bg-[#C26D5C]/10 border border-[#C26D5C]/30 rounded-full px-3 py-1.5"
            data-testid="cfo-books-badge"
          >
            <Sparkles size={12} strokeWidth={1.5} /> Founding members · Locked-in pricing · Premium
          </div>

          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F382E] leading-[1.05] tracking-tight mt-5">
            Upload your bank statements.
            <span className="italic text-[#C26D5C]"> We'll build the books.</span>
          </h1>

          <p className="text-base sm:text-lg text-[#1A1A1A] leading-relaxed mt-6 max-w-xl">
            Most owners don't have a clean P&L sitting around. <strong>CFO Books</strong> takes 3–12
            months of bank statement PDFs and reconstructs your income statement, cash flow, and
            letter grade — automatically. No bookkeeper required.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {PROMISES.map((p) => (
              <div
                key={p.title}
                className="bg-white border border-[#E5E0D8] rounded-xl p-5"
                data-testid={`promise-${p.title.toLowerCase().split(" ")[0]}`}
              >
                <p.icon size={20} strokeWidth={1.5} className="text-[#C26D5C]" />
                <h3 className="font-serif-display text-lg text-[#1F382E] mt-3 tracking-tight">
                  {p.title}
                </h3>
                <p className="text-sm text-[#5C665D] mt-2 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>

          {/* Founding member banner */}
          <div className="mt-10 bg-[#1F382E] text-[#F7F5F0] rounded-2xl p-7" data-testid="founding-member-banner">
            <div className="flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.5} className="text-[#C26D5C]" />
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#D4C9B6]">
                Founding-member offer
              </p>
            </div>
            <div className="mt-3 flex items-baseline gap-3 flex-wrap">
              <span className="font-serif-display text-5xl">$79</span>
              <span className="text-[#D4C9B6] text-lg">/ month · for 12 months</span>
            </div>
            <p className="text-sm text-[#D4C9B6] mt-2 leading-relaxed">
              Reserve now with a <strong className="text-[#F7F5F0]">$1 refundable deposit</strong> and lock in
              $79/mo for your first 12 months. Regular pricing starts at $99/mo on the Starter tier.
              Deposit applies as credit to your first month.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm text-[#D4C9B6] leading-relaxed">
              <li className="flex items-start gap-2">
                <CheckCircle2 size={14} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#C26D5C]" />
                <span>$1 refundable any time before launch.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={14} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#C26D5C]" />
                <span>Cancel any time after launch — no contracts.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={14} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#C26D5C]" />
                <span>Replaces a $300/mo bookkeeper for the financial-health read.</span>
              </li>
            </ul>
          </div>

          {/* Regular pricing tiers */}
          <div className="mt-10" data-testid="pricing-tiers">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
              Regular pricing — when you join after launch
            </p>
            <h2 className="font-serif-display text-2xl text-[#1F382E] mt-2 tracking-tight">
              Three tiers. Pick what fits.
            </h2>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              {TIERS.map((t) => (
                <div
                  key={t.name}
                  className={`rounded-xl p-5 border ${
                    t.accent
                      ? "bg-white border-[#C26D5C] shadow-[0_4px_16px_rgba(194,109,92,0.10)]"
                      : "bg-white border-[#E5E0D8]"
                  }`}
                  data-testid={`tier-${t.name.toLowerCase()}`}
                >
                  {t.accent && (
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium mb-1.5">
                      Most popular
                    </p>
                  )}
                  <p className="font-serif-display text-lg text-[#1F382E] tracking-tight">
                    {t.name}
                  </p>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="font-serif-display text-3xl text-[#1F382E]">{t.price}</span>
                    <span className="text-xs text-[#5C665D]">{t.sub}</span>
                  </div>
                  <p className="text-[12px] text-[#5C665D] mt-2 leading-relaxed">{t.blurb}</p>
                  <ul className="mt-4 space-y-1.5">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-[12.5px] text-[#1A1A1A]">
                        <CheckCircle2 size={12} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#2C5C43]" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — Reservation form */}
        <aside className="lg:col-span-5 lg:sticky lg:top-8">
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-7 shadow-sm">
            {preorderStatus ? (
              <div className="text-center" data-testid="preorder-success">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#C26D5C]/10 text-[#C26D5C]">
                  <Sparkles size={28} strokeWidth={1.5} />
                </div>
                <p className="font-serif-display text-2xl text-[#1F382E] mt-5 tracking-tight">
                  You're locked in.
                </p>
                <p className="text-sm text-[#5C665D] mt-2">
                  Position <strong className="text-[#1F382E]">#{preorderStatus.position}</strong> · $79/mo founding-member pricing reserved.
                </p>
                <div className="mt-5 p-4 bg-[#F7F5F0] border border-[#E5E0D8] rounded-lg text-left">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                    What happens next
                  </p>
                  <ul className="mt-2.5 space-y-1.5 text-sm text-[#1A1A1A]">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={13} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#2C5C43]" />
                      <span>Your $1 deposit is held by Stripe (refundable any time before launch).</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={13} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#2C5C43]" />
                      <span>At launch: $79/mo for your first 12 months, locked in.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={13} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#2C5C43]" />
                      <span>The $1 deposit applies as credit to your first month.</span>
                    </li>
                  </ul>
                </div>
                <p className="text-sm text-[#5C665D] mt-5 leading-relaxed">
                  We have <strong className="text-[#1F382E]">{preorderStatus.email}</strong> on file for launch updates.
                </p>
                <Link
                  to={`/cfo-books/dashboard?email=${encodeURIComponent(preorderStatus.email || email)}`}
                  className="block mt-6"
                >
                  <Button
                    className="w-full bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 transition-all duration-300"
                    data-testid="open-cfo-books-dashboard-btn"
                  >
                    Open CFO Books →
                  </Button>
                </Link>
                <Link to="/" className="block mt-2">
                  <Button
                    variant="outline"
                    className="w-full border-[#1F382E]/20 text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] rounded-md h-11 transition-all duration-300"
                  >
                    Back to home
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                  Reserve your spot
                </p>
                <h2 className="font-serif-display text-2xl text-[#1F382E] mt-2 tracking-tight">
                  Lock in $79/mo.
                </h2>
                <p className="text-sm text-[#5C665D] mt-2 leading-relaxed">
                  <strong className="text-[#1F382E]">$1 refundable deposit</strong> reserves your founding-member rate
                  for the first 12 months. Cancel any time. No contracts.
                </p>

                <div
                  className="mt-4 p-3 bg-[#C26D5C]/8 border border-[#C26D5C]/30 rounded-lg text-[11px] text-[#1A1A1A] leading-relaxed"
                  data-testid="reservation-explainer"
                >
                  <p>
                    <strong>Reservation requires payment.</strong> Stripe-secured $1 deposit holds your
                    $79/mo rate. Refundable any time before launch. Applies as credit to your first month.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="mt-5 space-y-3" data-testid="reservation-form">
                  <Input
                    type="email"
                    required
                    placeholder="you@business.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 bg-white border-[#E5E0D8] focus-visible:ring-[#1F382E]"
                    data-testid="reservation-email-input"
                  />
                  <Input
                    type="text"
                    placeholder="Business type (e.g. restaurant, SaaS, contractor)"
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                    className="h-11 bg-white border-[#E5E0D8] focus-visible:ring-[#1F382E]"
                    data-testid="reservation-business-input"
                    maxLength={100}
                  />
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D] block mb-1.5">
                      Your feedback shapes what we build
                    </label>
                    <textarea
                      placeholder="What's the most painful part of getting a clean view of your numbers today? Anything specific you'd want CFO Books to do?"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      maxLength={1000}
                      className="w-full bg-white border border-[#E5E0D8] focus:border-[#1F382E] focus:ring-2 focus:ring-[#1F382E]/20 rounded-md text-sm text-[#1A1A1A] px-3 py-2.5 resize-none outline-none transition-colors"
                      data-testid="reservation-notes-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={busy}
                    className="w-full bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 transition-all duration-300"
                    data-testid="reservation-submit-btn"
                  >
                    {busy ? (
                      <>
                        <Loader2 size={14} className="animate-spin mr-2" />
                        Redirecting to Stripe…
                      </>
                    ) : (
                      "Reserve with $1 deposit →"
                    )}
                  </Button>
                </form>

                <div className="flex items-start gap-2 text-[11px] text-[#5C665D] leading-relaxed mt-4">
                  <Lock size={11} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  <span>
                    Stripe-secured checkout. $1 deposit refundable any time before launch.
                  </span>
                </div>
              </>
            )}
          </div>

          <p className="text-[11px] text-[#5C665D] mt-4 leading-relaxed px-1">
            Already have your numbers in a spreadsheet or PDF?{" "}
            <Link to="/analyze" className="underline underline-offset-2 hover:text-[#1F382E] transition-colors">
              Run a free analysis now →
            </Link>
          </p>
          <p className="text-[11px] text-[#5C665D] mt-2 leading-relaxed px-1">
            Curious how the CFO Books dashboard works?{" "}
            <Link
              to="/cfo-books/dashboard?demo=1"
              className="underline underline-offset-2 hover:text-[#1F382E] transition-colors"
              data-testid="view-demo-link"
            >
              See the live demo →
            </Link>
          </p>
        </aside>
      </main>

      <SiteFootnote />
    </div>
  );
}

function upsertMeta(attr, key, value) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
